/**
 * Videocall Audio Response Handler
 *
 * Decodes base64 audio chunks from the voice provider (OpenAI / Grok)
 * and plays them through the Web Audio API with sample-accurate scheduling.
 */

/**
 * Decode and schedule an audio response chunk for playback.
 *
 * @param {string}  audioContent        Base64-encoded PCM16 audio data
 * @param {object}  ctx
 * @param {AudioContext} ctx.audioCtx    Web Audio API context
 * @param {number}  ctx.nextScheduledTime  Mutable reference — updated in-place
 * @param {function} ctx.debugLog        Logging function
 * @param {function} ctx.schedulePlayback Callback to schedule a buffer for playback: fn(buffer, startTime)
 */
export async function handleAudioResponse(audioContent, ctx) {
    const { audioCtx, nextScheduledTime, debugLog, schedulePlayback } = ctx;

    try {
        debugLog("🔊 Audio chunk received, playing immediately...");

        // Decode base64 to binary (PCM16 from OpenAI)
        const pcm16Data = Uint8Array.from(atob(audioContent), c => c.charCodeAt(0));
        debugLog(`📦 PCM16 bytes: ${pcm16Data.length} bytes`);

        // Convert PCM16 bytes to Int16 samples
        const samples = new Int16Array(pcm16Data.buffer);
        debugLog(`🎵 Audio samples: ${samples.length} samples`);

        // Create AudioBuffer directly from PCM16 samples
        const sampleRate = 24000; // OpenAI uses 24kHz
        const numChannels = 1; // Mono
        const buffer = audioCtx.createBuffer(numChannels, samples.length, sampleRate);

        // Convert Int16 to Float32 and copy to buffer
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < samples.length; i++) {
            channelData[i] = samples[i] / 32768.0; // Convert to -1.0 to 1.0 range
        }

        debugLog("✅ Audio buffer created:", {
            duration: buffer.duration,
            numberOfChannels: buffer.numberOfChannels,
            sampleRate: buffer.sampleRate
        });

        // Schedule playback using Web Audio API timing
        const now = audioCtx.currentTime;
        const startTime = Math.max(now, nextScheduledTime.val);
        nextScheduledTime.val = startTime + buffer.duration;

        debugLog(`📋 Scheduling audio at ${startTime.toFixed(3)} s(now: ${now.toFixed(3)}s, wait: ${(startTime - now).toFixed(3)}s)`);

        // Play buffer at scheduled time
        schedulePlayback(buffer, startTime);

    } catch (error) {
        console.error("❌ Error playing audio chunk:", error);
    }
}
